/**
 * Visual constants and alarm category configuration for the observability feature.
 *
 * Each category maps to a CloudWatch Composite Alarm with the same name as the
 * category label (created by ATMSMonitoringCDK ObservabilityStack).
 *
 * The UI calls the BFF once per category, passing the composite alarm name.
 * To add a new category: add a new entry here AND add the composite alarm in CDK.
 */
import { HealthStatus, STATUS_META } from '../types';

/**
 * Flat color lookup extracted from STATUS_META for use in inline styles.
 * Exists as a separate object so HistoryBar can use it as a simple
 * Record<HealthStatus, string> without reaching into STATUS_META's nested shape.
 */
export const STATUS_COLORS: Record<HealthStatus, string> = Object.fromEntries(
  Object.entries(STATUS_META).map(([key, meta]) => [key, meta.color])
) as Record<HealthStatus, string>;

export const TOOLTIP_STYLES = {
  backgroundColor: 'var(--awsui-color-background-container-content)',
  color: 'var(--awsui-color-text-body-default)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
} as const;

export const SLACK_BUTTON_STYLES = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: '#4A154B',
  color: '#fff',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
} as const;

/**
 * How often an open page re-fetches health data. Composite alarms re-evaluate every minute;
 * each refresh costs about 24 CloudWatch calls in the BFF, so a few minutes is the compromise.
 */
export const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Slack channel for alarm notifications — only shown to internal (Amazon) users.
 * Update this URL when the bot moves to a different channel.
 */
export const SLACK_CHANNEL_URL = 'https://amazon.enterprise.slack.com/archives/C0BJU5U0NQJ';

/**
 * Category configuration — single source of truth for categories in the UI.
 *
 * The composite alarm name equals the category label (set in CDK).
 * Each category maps to one BFF call: GET /api/observability/categoryAlarmHistory?alarmName=<label>
 */
export interface AlarmCategory {
  /** Matches the category id in the BFF's /api/observability/status response. */
  id: string;
  label: string;
  description: string;
}

/**
 * Status page categories in display order. Each is an Outage and a Degraded composite alarm in
 * ATMSMonitoringCDK lib/status-page-categories.ts, read by the BFF's STATUS_CATEGORIES.
 */
export const ALARM_CATEGORIES: AlarmCategory[] = [
  {
    id: 'signin',
    label: 'Sign-in and ATMS web app',
    description: 'Signing in to ATMS and loading its web pages, including the project management app.',
  },
  {
    id: 'api',
    label: 'ATMS API read traffic',
    description: 'API calls that read projects, jobs, users, analyses and translation memory segments.',
  },
  {
    id: 'import',
    label: 'Projects and file import',
    description: 'Creating projects and jobs, and importing source files into them.',
  },
  {
    id: 'pretranslate',
    label: 'Machine translation and pretranslation',
    description:
      'Pretranslating jobs from translation memory and machine translation, including projects created from templates.',
  },
  {
    id: 'analysis',
    label: 'Analysis and export',
    description:
      'Running analyses and quality checks, and the file conversion service that produces exported files.',
  },
  {
    id: 'tm',
    label: 'Translation memory',
    description:
      'Searching and updating translation memories, used for pretranslation and for matches in the editor.',
  },
  {
    id: 'termbase',
    label: 'Term base',
    description: 'Term base lookups, which suggest approved terminology while translating.',
  },
  {
    id: 'editor',
    label: 'Translation editor',
    description: 'The web editor where linguists translate and review jobs, including spell checking.',
  },
  {
    id: 'portal',
    label: 'Translator Portal and offers',
    description: 'The Translator Portal, and offering and assigning jobs to translators.',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description: 'Email notifications and the events that tell users about project and job updates.',
  },
  {
    id: 'scoring',
    label: 'Scoring',
    description: 'The quality scoring service that measures translation quality.',
  },
];
